import { Option } from "@inrev/core";
import { bondFormFileUrlQueryKey, useFetchBondTerms } from "@portal/api/bond";
import { useBondComments } from "@portal/api/comments";
import {
	bondTermOrdinal,
	CurrentBondTermCard,
	commissionPercentageForBond,
	deriveTermCommission,
	findCurrentBondTerm,
} from "@portal/components/features/bond-terms";
import { messageDraftKey } from "@portal/components/features/message-draft";
import { CommentsSection } from "@portal/components/layout/CommentsSection";
import { Card } from "@portal/components/primitives";
import { BondAttachmentsTable } from "@portal/components/shared/attachments/BondAttachmentsTable";
import type { FC } from "react";
import { AdminBondFormCard } from "../../../shared/AdminBondFormCard";
import { AdminBondProjectCard } from "../../../shared/AdminBondProjectCard";
import { AdminFollowerControls } from "../../../shared/AdminFollowerControls";
import { AdminBondClosingDetailsCard } from "../../components/AdminBondClosingDetailsCard";
import { AdminBondTermsCard } from "../../components/AdminBondTermsCard";
import type { BondPageBodyProps } from "../types";

export const AdminBondOverviewTab: FC<BondPageBodyProps> = ({
	bond,
	followersControl,
	commercialSuretyEnabled = false,
}) => {
	const { comments, commentsLoading, createComment, createCommentLoading } = useBondComments(
		bond.accountId,
		bond.id,
		true,
	);
	const { terms } = useFetchBondTerms(bond.accountId, bond.id, commercialSuretyEnabled);
	const currentTerm = findCurrentBondTerm(terms);

	return (
		<div className="flex flex-col space-y-[40px]" data-testid="admin-bond-page-body">
			<AdminBondTermsCard bond={bond} />
			{bond.status === "closed" && <AdminBondClosingDetailsCard bond={bond} />}
			{commercialSuretyEnabled && Option.isSome(currentTerm) && (
				<CurrentBondTermCard
					commission={deriveTermCommission(
						currentTerm.totalPremium,
						commissionPercentageForBond(bond),
					)}
					ordinal={bondTermOrdinal(terms, currentTerm)}
					term={currentTerm}
				/>
			)}
			<AdminBondProjectCard bond={bond} />
			{!commercialSuretyEnabled && (
				<AdminBondFormCard
					bondForm={bond.bondForm}
					status={bond.status}
					bondFormViewUrl={`/v2/admin/surety/bonds/${bond.id}/bond-form`}
					bondFormViewQueryKey={bondFormFileUrlQueryKey(bond.id, false)}
				/>
			)}
			<Card elevation="raised" className="rounded-xl" data-testid="admin-bond-messages-card">
				<Card.Body className="p-6">
					<CommentsSection
						entityType="Bond"
						exportEntityType="bond"
						comments={comments}
						commentsLoading={commentsLoading}
						createComment={createComment}
						createCommentLoading={createCommentLoading}
						draftKey={messageDraftKey("bonds", bond.id)}
						commentDownloadHeader={`${bond.displayTitle} For ${bond.principalName}`}
						downloadTrackingId="bond-admin-comments-download"
						headerEnd={
							<AdminFollowerControls
								{...followersControl}
								manageFollowersSubtext="Followers will receive email notifications for all comments and changes to the status of this bond"
								agencyId={bond.agencyId}
								trackingPrefix="bond-admin-comments-follow"
							/>
						}
					/>
				</Card.Body>
			</Card>
			<BondAttachmentsTable
				bondId={bond.id}
				accountId={bond.accountId}
				downloadAllName={`${bond.accountName} ${bond.displayTitle} Attachments`}
				allowDelete={true}
				allowUpload={true}
			/>
		</div>
	);
};
